import { Injectable, inject } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { PlanMetadataModel } from '@core/models/plan.model';
import { ParamsService } from '@core/services/params/params.service';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { isNil } from '@core/utils/shared-utils';

import { PlanFindingsActions } from './plan-findings.actions';
import { selectFindingsCheckContext } from './plan-findings.selectors';

interface FindingsContext {
  planId: string;
  paramId: number;
}

interface PlanSource {
  planId: string;
  parameterId: number | undefined;
  solverId: number | undefined;
}

function planSource(plan: PlanMetadataModel | undefined): PlanSource | null {
  if (isNil(plan)) return null;
  return { planId: plan.id, parameterId: plan.parameter?.id, solverId: plan.solver?.id };
}

/** Announces which plan and parameter set the findings describe, for every plan page. */
@Injectable()
export class PlanFindingsContextEffects {
  private readonly store = inject(Store);
  private readonly params = inject(ParamsService);

  /**
   * A context the slice already holds is not announced again: a plan refetch produces a new
   * metadata object every time, and re-announcing would reset the slice and discard the
   * staleness the user's own edit just recorded. Compared with the slice rather than with the
   * last announcement, because the slice is also reset by plan navigation and by the plan
   * leaving view, and the plan the user comes back to must then be announced again.
   */
  context$ = createEffect(() =>
    this.store.select(selectPlan).pipe(
      map((plan) => planSource(plan)),
      distinctUntilChanged(
        (a, b) =>
          a?.planId === b?.planId &&
          a?.parameterId === b?.parameterId &&
          a?.solverId === b?.solverId
      ),
      switchMap((source) =>
        isNil(source)
          ? EMPTY
          : this.contextOf(source).pipe(
              map((context) => ({ planId: source.planId, context }))
            )
      ),
      concatLatestFrom(() => this.store.select(selectFindingsCheckContext)),
      filter(
        ([{ context }, held]) =>
          isNil(context) ||
          context.planId !== held?.planId ||
          context.paramId !== held?.paramId
      ),
      map(([{ planId, context }]) =>
        isNil(context)
          ? PlanFindingsActions.contextUnavailable({ planId })
          : PlanFindingsActions.contextChanged(context)
      )
    )
  );

  /**
   * A plan produced by an optimization carries the parameter set it was produced with, so its
   * findings answer for that configuration rather than whichever set the solver lists first.
   */
  private contextOf(source: PlanSource): Observable<FindingsContext | null> {
    const { planId, parameterId, solverId } = source;
    if (!isNil(parameterId)) return of({ planId, paramId: parameterId });
    if (isNil(solverId)) return of(null);
    return this.params.getParamsBySolverId$(Number(solverId)).pipe(
      map((list) => list?.result?.[0]?.id),
      map((paramId) => (isNil(paramId) ? null : { planId, paramId })),
      catchError((error) => {
        console.error('Error in parameters:', error);
        return of(null);
      })
    );
  }
}
